// src/pages/Settings.js
import { useState, useEffect } from "react";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { LIFECYCLE_STAGES } from "../lib/constants";
import { CRM_OPERATORS } from "../lib/crmUsers";
import { crmBackfillAllProfileNamesCallable } from "../lib/crmApi";
import PageTabs from "../components/PageTabs";

const INTEGRATIONS = [
  { id:"firebase", name:"Firebase", icon:"🔥", status:"connected", detail:"Auth (CRM shell login + custom token) + Firestore" },
  { id:"stripe",   name:"Stripe",   icon:"⚡", status:"connected", detail:"Payment links & refunds via Cloud Functions secrets" },
  { id:"sendgrid", name:"SendGrid", icon:"✉",  status:"connected",   detail:"API key in Functions · scan promo automation optional" },
  { id:"gcs",      name:"Google Cloud Storage", icon:"☁", status:"connected", detail:"Bucket: scanbrix-scans · Auto-syncing .obj files" },
];

const TABS = ["Team", "Integrations", "Lifecycle stages", "Custom fields", "Automations"];

const AUTO_REF = doc(db, "crm_settings", "automations");

export default function Settings() {
  const [tab, setTab] = useState("Team");
  const [auto, setAuto] = useState({
    scanNoOrderPromoEnabled: false,
    promoCode: "",
    promoFromEmail: "",
    promoSubject: "",
    promoHtmlBody: "",
  });
  const [autoSaving, setAutoSaving] = useState(false);
  const [nameBackfillBusy, setNameBackfillBusy] = useState(false);
  const [nameBackfillLog, setNameBackfillLog] = useState("");

  useEffect(() => {
    return onSnapshot(AUTO_REF, (snap) => {
      if (snap.exists()) setAuto((prev) => ({ ...prev, ...snap.data() }));
    });
  }, []);

  async function runProfileNameBackfill() {
    setNameBackfillBusy(true);
    setNameBackfillLog("");
    const fn = crmBackfillAllProfileNamesCallable();
    let authBatches = 0;
    let authUpdated = 0;
    let fsBatches = 0;
    let fsUpdated = 0;
    try {
      let pageToken = undefined;
      do {
        const { data } = await fn({ phase: "auth", maxResults: 100, pageToken });
        authBatches += 1;
        authUpdated += data.profilesUpdated || 0;
        pageToken = data.nextPageToken || null;
        setNameBackfillLog(
          `Auth: batch ${authBatches}, ${authUpdated} profile row(s) updated…`
        );
      } while (pageToken);

      let startAfterId = undefined;
      do {
        const { data } = await fn({ phase: "firestore", startAfterId, limit: 200 });
        fsBatches += 1;
        fsUpdated += data.updated || 0;
        startAfterId = data.nextStartAfterId || null;
        setNameBackfillLog(
          `Auth done (${authUpdated} updated). Firestore: batch ${fsBatches}, ${fsUpdated} updated…`
        );
      } while (startAfterId);

      setNameBackfillLog(
        `Done. Auth: ${authBatches} batch(es), ${authUpdated} row(s). Firestore: ${fsBatches} batch(es), ${fsUpdated} row(s) filled from names on file.`
      );
    } catch (e) {
      setNameBackfillLog(e?.message || String(e));
    } finally {
      setNameBackfillBusy(false);
    }
  }

  async function saveAutomations() {
    setAutoSaving(true);
    try {
      await setDoc(
        AUTO_REF,
        {
          ...auto,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } finally {
      setAutoSaving(false);
    }
  }

  return (
    <div className="page-wrap" style={{ maxWidth: 720 }}>
      <header className="page-header">
        <h1 className="page-header-title">Settings</h1>
        <p className="page-header-desc">Team, integrations, lifecycle stages, and automations.</p>
      </header>
      <PageTabs tabs={TABS.map((t) => ({ id: t, label: t }))} value={tab} onChange={setTab} />

      {/* ── Team ── */}
      {tab === "Team" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Operators</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2, lineHeight: 1.5 }}>
              This CRM uses a shared shell password (see <code style={{ fontSize: 11 }}>functions/shellAuth.js</code>). Sign-in accounts:
            </div>
          </div>
          <div className="card" style={{ padding: 0 }}>
            {CRM_OPERATORS.map((u, i) => (
              <div
                key={u.email}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 20px",
                  borderBottom: i < CRM_OPERATORS.length - 1 ? "0.5px solid var(--border)" : "none",
                }}
              >
                <div className="avatar" style={{ background: "var(--accent-dim)", color: "var(--accent)" }}>
                  {u.name.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>{u.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Integrations ── */}
      {tab === "Integrations" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {INTEGRATIONS.map(int => (
            <div key={int.id} className="card card-sm" style={{display:"flex",alignItems:"center",gap:14}}>
              <div style={{
                width:42,height:42,borderRadius:10,fontSize:20,
                background:"var(--bg-elevated)",border:"0.5px solid var(--border)",
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0
              }}>{int.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:500,marginBottom:2}}>{int.name}</div>
                <div style={{fontSize:12,color:"var(--text-3)"}}>{int.detail}</div>
              </div>
              {int.status === "connected" ? (
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:"var(--green)",flexShrink:0,display:"inline-block"}}/>
                  <span style={{fontSize:12,color:"var(--green)"}}>Connected</span>
                </div>
              ) : (
                <button type="button" className="btn btn-primary btn-sm">Connect</button>
              )}
            </div>
          ))}
          <div className="card card-sm" style={{ marginTop: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Contact names</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.55, marginBottom: 12 }}>
              Copies names from Firebase Auth (incl. Google/Apple) and from profile{" "}
              <code style={{ fontSize: 11 }}>first_name</code>/<code style={{ fontSize: 11 }}>last_name</code>{" "}
              into the CRM <code style={{ fontSize: 11 }}>name</code> field when it was missing or showed only an email.
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={nameBackfillBusy}
              onClick={() => runProfileNameBackfill()}
            >
              {nameBackfillBusy ? "Running backfill…" : "Backfill all contact names"}
            </button>
            {nameBackfillLog ? (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: "var(--text-2)",
                  lineHeight: 1.5,
                  padding: 10,
                  background: "var(--bg-elevated)",
                  borderRadius: 8,
                  border: "0.5px solid var(--border)",
                }}
              >
                {nameBackfillLog}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* ── Lifecycle stages ── */}
      {tab === "Lifecycle stages" && (
        <div>
          <div style={{fontSize:13,color:"var(--text-2)",marginBottom:16,lineHeight:1.6}}>
            Stages advance automatically based on Firebase and Stripe events. You can also move contacts manually from their profile.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {LIFECYCLE_STAGES.map((s,i) => (
              <div key={s.id} className="card card-sm">
                <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                  <div style={{
                    width:32,height:32,borderRadius:8,flexShrink:0,
                    background:s.bg,display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:16,color:s.color,fontWeight:600
                  }}>{i+1}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:s.color,marginBottom:2}}>{s.label}</div>
                    <div style={{fontSize:12,color:"var(--text-3)",marginBottom:8}}>{s.description}</div>
                    {s.autoTransitions.length > 0 && (
                      <div style={{fontSize:11,color:"var(--text-2)"}}>
                        <span style={{color:"var(--text-3)"}}>Auto-advances to next when:</span>{" "}
                        {s.autoTransitions.map(t => (
                          <span key={t.trigger} style={{
                            fontFamily:"var(--mono)",fontSize:11,
                            background:"var(--bg-elevated)",padding:"1px 6px",
                            borderRadius:4,color:"var(--amber)"
                          }}>{t.trigger}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {i < LIFECYCLE_STAGES.length-1 && (
                    <div style={{fontSize:10,color:"var(--text-3)",marginTop:4}}>↓</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Automations (Firestore → Cloud Function crmScanPromoEmail) ── */}
      {tab === "Automations" && (
        <div>
          <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 16, lineHeight: 1.6 }}>
            When <strong>enabled</strong>, the <code style={{ fontSize: 11 }}>crmScanPromoEmail</code> function (codebase{" "}
            <code style={{ fontSize: 11 }}>crm</code>) runs on new <code style={{ fontSize: 11 }}>scans</code> documents. If the
            scan has no order fields and the user has an email in <code style={{ fontSize: 11 }}>users/{`{uid}`}</code>, SendGrid
            sends the promo email once per scan. A row is added to <code style={{ fontSize: 11 }}>email_activity</code> on the user.
            <br />
            <br />
            <strong>Support@ → tickets:</strong> use SendGrid Inbound Parse or forwarding to a small webhook that writes to{" "}
            <code style={{ fontSize: 11 }}>support_tickets</code>; this CRM includes manual ticket UI on the Support page.
          </div>
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!auto.scanNoOrderPromoEnabled}
                onChange={(e) => setAuto((a) => ({ ...a, scanNoOrderPromoEnabled: e.target.checked }))}
              />
              Send promo email when a scan is uploaded without an order
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Promo code (inserted in default HTML)
              <input
                className="input"
                value={auto.promoCode || ""}
                onChange={(e) => setAuto((a) => ({ ...a, promoCode: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              From email (verified sender in SendGrid)
              <input
                className="input"
                type="email"
                placeholder="noreply@yourdomain.com"
                value={auto.promoFromEmail || ""}
                onChange={(e) => setAuto((a) => ({ ...a, promoFromEmail: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Subject
              <input
                className="input"
                value={auto.promoSubject || ""}
                onChange={(e) => setAuto((a) => ({ ...a, promoSubject: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Custom HTML body (optional — overrides default template)
              <textarea
                className="textarea"
                value={auto.promoHtmlBody || ""}
                onChange={(e) => setAuto((a) => ({ ...a, promoHtmlBody: e.target.value }))}
                style={{ marginTop: 4, minHeight: 100 }}
              />
            </label>
            <button type="button" className="btn btn-primary btn-sm" disabled={autoSaving} onClick={saveAutomations}>
              {autoSaving ? "Saving…" : "Save automations"}
            </button>
          </div>
        </div>
      )}

      {/* ── Custom fields ── */}
      {tab === "Custom fields" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:"var(--text-2)"}}>Custom fields appear on every contact profile.</div>
            <button className="btn btn-primary btn-sm">+ Add field</button>
          </div>
          <div className="card" style={{padding:0}}>
            {[
              { name:"Building Type",      type:"dropdown", options:"Commercial, Residential, Industrial, Mixed Use, Studio" },
              { name:"Preferred Contact",  type:"dropdown", options:"Email, Phone, In-app" },
              { name:"Referral Source",    type:"text",     options:"" },
              { name:"Contract Value",     type:"number",   options:"" },
            ].map((f,i,arr) => (
              <div key={f.name} style={{
                display:"flex",alignItems:"center",gap:12,padding:"13px 20px",
                borderBottom: i<arr.length-1 ? "0.5px solid var(--border)" : "none"
              }}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500}}>{f.name}</div>
                  {f.options && <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>Options: {f.options}</div>}
                </div>
                <span className="badge badge-muted">{f.type}</span>
                <button className="btn btn-ghost btn-sm">Edit</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
